/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Cookies } from "react-cookie";

import UserSlice, { UserSliceFromRoot } from "types/UserSlice";

import axios from "connection";

import { fetchConfigFromAPI, redirectToID, setLoader, setModal } from "state/config-slice";
import getDeeplinkToken from "utils/getDeeplinkToken";
import { setDebugInfo } from "utils/debugHelper";

import { API_URL, PPE_PATH } from "config";
import AccountContract from "types/AccountContract";

const initialState = {
  loaded: false,
  loggedIn: false,
  account: {},
  choosenAccountContract: null,
  mustSelectContract: false,
} as UserSlice;

export const fetchUserFromAPI = createAsyncThunk("user/fetchUser", async (_arg, { dispatch }: any) => {
  try {
    const response = await axios.get(`${API_URL}${PPE_PATH}/account?${getDeeplinkToken()}`);

    if (response.status === 200) {
      const account = response.data;
      setDebugInfo("account", account);
      return { loggedIn: true, account, loaded: true };
    }

    return { loggedIn: false, account: {}, loaded: true };
  } catch (error: any) {
    if (error?.response?.status === 401) {
      dispatch(setLoader({ showLoader: true, messageHeader: "", messageText: "" }));
      dispatch(redirectToID(null));

      const cookies = new Cookies();
      cookies.remove("CART", { path: "/" });

      return { loggedIn: false, account: {}, loaded: true };
    }

    dispatch(
      setModal({
        showModal: true,
        errorModal: true,
      }),
    );

    return Promise.reject(error);
  }
});

function htmlDecode(input: string) {
  const doc = new DOMParser().parseFromString(input, "text/html");

  return doc.documentElement.textContent?.replace("#", "") || "";
}

export const logoutUserFromAPI = createAsyncThunk("user/logoutUser", async (_arg, { dispatch }: any) => {
  try {
    dispatch(setLoader({ showLoader: true }));

    const result = await axios.post(`${API_URL}${PPE_PATH}/logout`, {});

    if (result.status === 200) {
      dispatch(fetchConfigFromAPI());
      const redirectURLfromID = result.data;

      return {
        loggedIn: false,
        redirectUrl: `${htmlDecode(redirectURLfromID)}`,
      };
    }

    dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
    return { loggedIn: true };
  } catch (error) {
    dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
    return Promise.reject(error);
  }
});

export const selectContractAPI = createAsyncThunk(
  "user/selectContract",
  async (accountContract: AccountContract, { dispatch }: any) => {
    try {
      dispatch(setLoader({ showLoader: true }));
      const response = await axios.post(`${API_URL}${PPE_PATH}/account`, {
        ...accountContract,
      });

      if (response.status === 200) {
        dispatch(setLoader({ showLoader: false }));
        return { contractSelectedSuccess: true };
      }
    } catch (error: any) {
      dispatch(setLoader({ showLoader: false }));
      return { contractSelectedSuccess: false };
    }
  },
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    selectAccountContract: (state, action) => {
      return {
        ...state,
        choosenAccountContract: action.payload,
      };
    },
    setMustSelectContract: (state, action) => {
      return {
        ...state,
        mustSelectContract: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserFromAPI.fulfilled, (state, action) => {
        state.account = action.payload.account;
        if (action.payload.account.accountContracts.length == 1) {
          state.choosenAccountContract = action.payload.account.accountContracts[0];
        } else {
          state.choosenAccountContract = null;
        }

        state.loaded = action.payload.loaded;
        state.loggedIn = action.payload.loggedIn;

        state.mustSelectContract = state.choosenAccountContract == null;
        setDebugInfo("state", state);
      })
      .addCase(fetchUserFromAPI.rejected, (state) => {
        state.loaded = true;
      })
      .addCase(logoutUserFromAPI.pending, (state) => {
        state.loaded = false;
        state.loggedIn = true;
      })
      .addCase(selectContractAPI.fulfilled, (state) => {
        state.mustSelectContract = false;
      })
      .addCase(logoutUserFromAPI.fulfilled, () => {
        return { ...initialState };
      });
  },
});

export const { selectAccountContract, setMustSelectContract } = userSlice.actions;

export const userState = (state: UserSliceFromRoot): UserSlice => state.user;

export default userSlice.reducer;
